// ============================================================================
// Cliente API para /api/gastos — usado por purchases.tsx (módulo Gastos).
// Antes el módulo guardaba todo en memoria (MOCK_PURCHASES). Ahora persiste
// en la BD vía /api/gastos.
// ============================================================================
import { api, apiBaseUrl, tokenStore } from './client';
import type { RespuestaPendiente } from './aprobaciones';

export type GastoTipo = 'ingredient_base' | 'service' | 'other';
export type GastoMetodoPago = 'cash' | 'transfer' | 'card' | 'credit' | 'check';

export interface Gasto {
  id: number;
  sede_id: number;
  tipo: GastoTipo;
  categoria: string;
  descripcion: string;
  monto: number;
  cantidad: number | null;
  unidad: string | null;
  metodo_pago: GastoMetodoPago;
  factura: string | null;
  factura_foto: string | null;
  proveedor: string | null;
  notas: string | null;
  ingrediente_id: number | null;
  fecha: string;            // YYYY-MM-DD
  creado_por: string | null;
  created_at: string;
  updated_at: string;
}

export interface GastoCreateInput {
  sede_id: number;
  tipo: GastoTipo;
  categoria?: string;
  descripcion: string;
  monto: number;
  cantidad?: number | null;
  unidad?: string | null;
  metodo_pago: GastoMetodoPago;
  factura?: string | null;
  factura_foto?: string | null;
  proveedor?: string | null;
  notas?: string | null;
  ingrediente_id?: number | null;
  fecha?: string;
  creado_por?: string | null;
  /** Items del inventario que entran con esta compra (suman al stock).
   *  costo = valor TOTAL de la línea en la factura (pesos, opcional) —
   *  alimenta el costo real por insumo de Parametrización (ago-2026). */
  items?: Array<{ item_id: number; cantidad: number; costo?: number }>;
}

export interface ListGastosQuery {
  sede_id: number;
  tipo?: GastoTipo;
  desde?: string;
  hasta?: string;
  limit?: number;
}

export const listGastos = (q: ListGastosQuery) => {
  const qs = new URLSearchParams({ sede_id: String(q.sede_id) });
  if (q.tipo) qs.set('tipo', q.tipo);
  if (q.desde) qs.set('desde', q.desde);
  if (q.hasta) qs.set('hasta', q.hasta);
  if (q.limit) qs.set('limit', String(q.limit));
  return api.get<Gasto[]>(`/api/gastos?${qs.toString()}`);
};

export const crearGasto = (input: GastoCreateInput) =>
  api.post<Gasto>('/api/gastos', input);

// Cajero (oct-2026): responde 202 RespuestaPendiente; el gasto NO se borra
// hasta que el admin de la sede lo apruebe.
/** Lo que el borrado descontó del inventario (oct-2026): una fila por item de la compra. */
export interface ReversoCompra { item_id: number; nombre: string; unidad: string; cantidad: number; saldo: number }

export const eliminarGasto = (id: number) =>
  api.del<{ ok: boolean; reversos?: ReversoCompra[] } | RespuestaPendiente>(`/api/gastos/${id}`);

// --- Items de inventario que entraron con un gasto (detail view) ---
export interface GastoItem {
  /** id de la línea (gastos_items) — necesario para corregirla. */
  id: number;
  item_id: number;
  cantidad: number;
  /** Valor total de la línea en la factura (pesos); null en gastos viejos. */
  costo: number | null;
  codigo: string;
  nombre: string;
  unidad: string;
  categoria: string | null;
}

export const getGastoItems = (id: number) =>
  api.get<GastoItem[]>(`/api/gastos/${id}/items`);

/** Resultado de corregir una línea: incluye qué pasó con el STOCK. */
export interface CorreccionLinea {
  ok: boolean;
  id: number;
  item_id: number;
  cantidad: number;
  costo: number | null;
  /** Diferencia aplicada al inventario (puede ser negativa). */
  stock_delta: number;
  /** Saldo del item tras el ajuste; null si la cantidad no cambió. */
  stock_resultante: number | null;
}

/** Corrige cantidad y/o costo de una línea de compra YA registrada.
 *  El backend ajusta el stock por la diferencia y lo deja en la auditoría. */
export const corregirGastoItem = (
  gastoId: number,
  lineaId: number,
  cambios: { cantidad?: number; costo?: number | null },
) => api.patch<CorreccionLinea>(`/api/gastos/${gastoId}/items/${lineaId}`, cambios);

/** Detalle de UN gasto por id — deep-link desde Parametrización (ago-2026). */
export const getGasto = (id: number) =>
  api.get<Gasto>(`/api/gastos/${id}`);

// --- Proveedores reutilizables ---
export interface Proveedor {
  id: number;
  sede_id: number;
  nombre: string;
  nit: string | null;
  telefono: string | null;
  email: string | null;
  notas: string | null;
}

export const listProveedores = (sedeId: number) =>
  api.get<Proveedor[]>(`/api/gastos/proveedores?sede_id=${sedeId}`);

export const crearProveedor = (input: Partial<Proveedor> & { sede_id: number; nombre: string }) =>
  api.post<Proveedor>('/api/gastos/proveedores', input);

// Descarga el Excel de Costos y Gastos respetando los filtros activos.
export async function descargarGastos(q: {
  sede_id: number; tipo?: string; categoria?: string; desde?: string; hasta?: string;
}) {
  const qs = new URLSearchParams({ sede_id: String(q.sede_id) });
  if (q.tipo) qs.set('tipo', q.tipo);
  if (q.categoria) qs.set('categoria', q.categoria);
  if (q.desde) qs.set('desde', q.desde);
  if (q.hasta) qs.set('hasta', q.hasta);
  const token = tokenStore.get();
  const r = await fetch(`${apiBaseUrl}/api/gastos/export?${qs.toString()}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!r.ok) throw new Error(`HTTP ${r.status}: ${await r.text()}`);
  const blob = await r.blob();
  const cd = r.headers.get('content-disposition') || '';
  const m = cd.match(/filename="([^"]+)"/);
  const fname = m?.[1] || 'costos_gastos.xlsx';
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = fname;
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
}
